import { useState, useMemo } from 'react';
import { threadsApi, ThreadListItem } from '../api/threads';

interface UseThreadManagerParams {
  selectedUserId: string | null;
  onError: (msg: string) => void;
}

export function useThreadManager({ selectedUserId, onError }: UseThreadManagerParams) {
  const [threads, setThreads] = useState<ThreadListItem[]>([]);
  const [threadsLoading, setThreadsLoading] = useState(false);
  const [cloningThreadId, setCloningThreadId] = useState<string | null>(null);

  // Pinned threads state (persisted in localStorage per user)
  const getPinnedKey = (userId: string) => `playground_pinned_${userId}`;
  const loadPinnedThreads = (userId: string): Set<string> => {
    try {
      const stored = localStorage.getItem(getPinnedKey(userId));
      return stored ? new Set(JSON.parse(stored)) : new Set();
    } catch {
      return new Set();
    }
  };
  const [pinnedThreadIds, setPinnedThreadIds] = useState<Set<string>>(() =>
    selectedUserId ? loadPinnedThreads(selectedUserId) : new Set(),
  );

  // Thread summary state
  const [threadSummary, setThreadSummary] = useState<string | null>(null);
  const [threadStats, setThreadStats] = useState<import('../components/ChatComponents').ThreadStats | null>(null);
  const [summaryExpanded, setSummaryExpanded] = useState(false);

  const togglePinThread = (threadId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setPinnedThreadIds((prev) => {
      const next = new Set(prev);
      if (next.has(threadId)) {
        next.delete(threadId);
      } else {
        next.add(threadId);
      }
      if (selectedUserId) {
        localStorage.setItem(getPinnedKey(selectedUserId), JSON.stringify([...next]));
      }
      return next;
    });
  };

  // Sort threads: pinned first, then by updated_at desc
  const sortedThreads = useMemo(
    () =>
      [...threads].sort((a, b) => {
        const aPinned = pinnedThreadIds.has(a.thread_id);
        const bPinned = pinnedThreadIds.has(b.thread_id);
        if (aPinned && !bPinned) return -1;
        if (!aPinned && bPinned) return 1;
        return 0;
      }),
    [threads, pinnedThreadIds],
  );

  const reloadPinnedThreads = (userId: string | null) => {
    setPinnedThreadIds(userId ? loadPinnedThreads(userId) : new Set());
  };

  const loadThreads = async (
    userId: string,
    opts?: { selectedThreadId: string | null; onAutoSelect: (threadId: string) => void },
  ) => {
    try {
      setThreadsLoading(true);
      const response = await threadsApi.listByUser(userId, { limit: 50, order: 'desc' });
      setThreads(response.threads);
      // Auto-select first thread if URL doesn't specify one
      if (opts && !opts.selectedThreadId && response.threads.length > 0) {
        opts.onAutoSelect(response.threads[0].thread_id);
      }
    } catch (err) {
      console.error('Failed to load threads:', err);
      setThreads([]);
    } finally {
      setThreadsLoading(false);
    }
  };

  const handleCreateThread = async (callbacks: {
    onCreated: (threadId: string) => void;
    clearMessages: () => void;
  }) => {
    if (!selectedUserId) return;
    try {
      onError('');
      const newThread = await threadsApi.create(selectedUserId, {
        title: `대화 ${threads.length + 1}`,
      });
      await loadThreads(selectedUserId);
      callbacks.onCreated(newThread.thread_id);
      callbacks.clearMessages();
    } catch (err) {
      console.error('Failed to create thread:', err);
      onError('대화 스레드 생성에 실패했습니다.');
    }
  };

  const handleDeleteThread = async (
    threadId: string,
    e: React.MouseEvent,
    callbacks: {
      selectedThreadId: string | null;
      onDeselect: () => void;
      clearMessages: () => void;
    },
  ) => {
    e.stopPropagation();
    if (!confirm('이 대화 스레드를 삭제하시겠습니까?')) return;

    try {
      await threadsApi.delete(threadId);
    } catch (err) {
      console.warn('Thread may have been already deleted:', err);
    }

    if (callbacks.selectedThreadId === threadId) {
      callbacks.onDeselect();
      callbacks.clearMessages();
    }
    if (selectedUserId) await loadThreads(selectedUserId);
  };

  const handleCloneThread = async (
    threadId: string,
    e: React.MouseEvent,
    callbacks: {
      onCloned: (threadId: string) => void;
      clearMessages: () => void;
    },
  ) => {
    e.stopPropagation();
    if (cloningThreadId) return; // double-submit guard (clone is a multi-second op)
    if (!confirm('이 스레드를 복제하시겠습니까?\n메시지·아카이브·체크포인트를 그대로 복제하며, 원본 사용자 안에 새 스레드로 생성됩니다.')) return;
    try {
      setCloningThreadId(threadId);
      onError('');
      const result = await threadsApi.clone(threadId);
      // Clone lives under the same user → refetch this user's threads and select it.
      if (selectedUserId) await loadThreads(selectedUserId);
      callbacks.onCloned(result.thread_id);
      callbacks.clearMessages();
    } catch (err) {
      console.error('Failed to clone thread:', err);
      onError(`스레드 복제에 실패했습니다: ${err instanceof Error ? err.message : ''}`);
    } finally {
      setCloningThreadId(null);
    }
  };

  const handleRenameThread = async (threadId: string, newTitle: string) => {
    try {
      await threadsApi.update(threadId, { title: newTitle });
      setThreads(prev => prev.map(t =>
        t.thread_id === threadId ? { ...t, title: newTitle } : t
      ));
    } catch (err) {
      console.error('Failed to rename thread:', err);
      onError('스레드 이름 변경에 실패했습니다.');
    }
  };

  return {
    threads,
    setThreads,
    threadsLoading,
    pinnedThreadIds,
    reloadPinnedThreads,
    togglePinThread,
    sortedThreads,
    threadSummary,
    setThreadSummary,
    threadStats,
    setThreadStats,
    summaryExpanded,
    setSummaryExpanded,
    loadThreads,
    handleCreateThread,
    handleDeleteThread,
    handleCloneThread,
    cloningThreadId,
    handleRenameThread,
  };
}
